import { useEffect, useState } from 'react';
import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { Select } from '../../../../components/inputs/Select';
import { TextField } from '../../../../components/inputs/TextField';
import { Textarea } from '../../../../components/inputs/Textarea';
import { ApiError } from '../../../../lib/apiError';
import { toDateTimeLocal } from '../../../../lib/formatDate';
import { focusValidationError } from '../../../../lib/validationFocus';
import { evidenceItemsApi, evidenceTypesApi } from '../evidenceApi';
import type { EvidenceItemDetail, EvidenceItemListItem, EvidenceTermOption } from '../evidenceTypes';
import { useStaffOptions } from '../../../human-resources/personnel/useStaff';

/**
 * Corrects what an item IS — what it is, its description, when and by whom it was acquired.
 * Never where it is or who holds it: those move only by a custody line, and the request has no
 * field for them. Never its KIND either: the kind decided at intake whether it is a shelf or a file.
 */
export function EvidenceItemModal({
  item,
  onClose,
  onSaved,
}: {
  item: EvidenceItemListItem;
  onClose: () => void;
  onSaved: (saved: EvidenceItemDetail) => void;
}) {
  const { options: staff, loading: loadingStaff } = useStaffOptions(true);

  const [description, setDescription] = useState(item.description);
  const [agentId, setAgentId] = useState(item.agentPersonnelId ?? '');
  const [typeId, setTypeId] = useState(item.evidenceTypeId ?? '');
  const [containerNumber, setContainerNumber] = useState(item.containerNumber ?? '');
  const [acquiredAt, setAcquiredAt] = useState(toDateTimeLocal(item.acquiredAt));
  const [types, setTypes] = useState<EvidenceTermOption[]>([]);
  const [loadingTypes, setLoadingTypes] = useState(true);

  useEffect(() => {
    const controller = new AbortController();

    // A failed load names no field, so it never reaches the save path's error state.
    evidenceTypesApi
      .options(controller.signal)
      .then((next) => {
        if (!controller.signal.aborted) setTypes(next);
      })
      .catch(() => undefined)
      .finally(() => {
        if (!controller.signal.aborted) setLoadingTypes(false);
      });

    return () => controller.abort();
  }, []);

  /*
    A RETIRED type the item already carries stays answerable: it is injected as an option, so the
    field states its name, and the server leaves a type it is not asked to change alone.
  */
  const typeOptions = [
    ...(item.evidenceTypeId && !types.some((t) => t.id === item.evidenceTypeId)
      ? [{ value: item.evidenceTypeId, label: item.typeName ?? '' }]
      : []),
    ...types.map((t) => ({ value: t.id, label: t.name })),
  ];
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const canSave =
    description.trim() !== '' &&
    containerNumber.trim() !== '' &&
    agentId !== '' &&
    typeId !== '' &&
    acquiredAt !== '' &&
    confirmed &&
    !saving;

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    try {
      onSaved(
        await evidenceItemsApi.update(item.id, {
          description: description.trim(),
          containerNumber: containerNumber.trim(),
          agentPersonnelId: agentId,
          evidenceTypeId: typeId,
          acquiredAt: new Date(acquiredAt).toISOString(),
        }),
      );
    } catch (caught) {
      const failure = caught instanceof ApiError ? caught : new ApiError('Could not save the item.', 0);

      setError(failure);
      focusValidationError(failure, { idPrefix: 'evidence-item' });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={`Edit ${item.inventoryNumber}`}
      size="lg"
      id="evidence-item-modal"
      autogenerate={{
        onGenerate: () => {
          setDescription(`${item.description.replace(/ \(corrected [A-Z0-9]+\)$/, '')} (corrected ${Math.random().toString(36).slice(2, 8).toUpperCase()})`);
          const other = staff.find((s) => s.value !== agentId) ?? staff[0];
          if (other) setAgentId(other.value);
          const otherType = types.find((t) => t.id !== typeId) ?? types[0];
          if (otherType) setTypeId(otherType.id);
          if (containerNumber.trim() === '') setContainerNumber(`SAMPLE-BOX-${Math.random().toString(36).slice(2, 8).toUpperCase()}`);
          if (acquiredAt === '') setAcquiredAt(toDateTimeLocal(new Date(Date.now() - 3_600_000)));
        },
        disabled: saving || loadingStaff || loadingTypes,
      }}
      confirm={{ id: 'evidence-item-confirm', checked: confirmed, onChange: setConfirmed, disabled: saving }}
      footer={
        <>
          <Button id="evidence-item-cancel" variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            id="evidence-item-submit"
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel="Submitting"
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        <div className="grid gap-4 sm:grid-cols-2">
          <Select
            id="evidence-item-evidence-type-id"
            label="Type"
            required
            description="What the item is — a knife, a substance, a phone."
            placeholder="Choose a type"
            value={typeId}
            onChange={setTypeId}
            options={typeOptions}
            remote
            loading={loadingTypes}
            disabled={saving}
            error={error?.fieldError('evidenceTypeId')}
          />

          <TextField
            id="evidence-item-acquired-at"
            label="Acquired"
            required
            type="datetime-local"
            description="When the evidence was acquired. It cannot be in the future."
            value={acquiredAt}
            onChange={(e) => setAcquiredAt(e.target.value)}
            disabled={saving}
            className="font-mono tabular-nums"
            error={error?.fieldError('acquiredAt')}
          />
        </div>

        <TextField
          id="evidence-item-container-number"
          label="Container/Box No"
          required
          description="The container or box it is kept in. Changing it moves the item to that group on the case's Evidence tab — custody is not affected."
          value={containerNumber}
          onChange={(e) => setContainerNumber(e.target.value)}
          maxLength={50}
          disabled={saving}
          className="font-mono"
          error={error?.fieldError('containerNumber')}
        />

        <Select
          id="evidence-item-agent-personnel-id"
          label="Acquired by"
          required
          description="The agent who acquired the evidence."
          placeholder="Choose an employee"
          value={agentId}
          onChange={setAgentId}
          options={staff}
          remote
          loading={loadingStaff}
          disabled={saving}
          error={error?.fieldError('agentPersonnelId')}
        />

        <Textarea
          id="evidence-item-description"
          label="Description"
          required
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          maxLength={2000}
          disabled={saving}
          error={error?.fieldError('description')}
        />

        <p className="text-xs text-slate-500">
          Where the evidence is and who holds it are not corrected here — they change only by a
          movement, which is approved and recorded on the chain of custody.
        </p>
      </div>
    </Modal>
  );
}
